import axios from "axios";
import TokenService from "./token.service";
import UserService from "./user.service";
import { API_URL } from "src/Common/function";
import { getActiveCompany } from "src/Common/tenant";
import { setAuthState } from "src/Redux/Slices/AuthSlice";
import { store } from "src/Redux/store";

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(
  (config) => {
    // The entered tenant for a Super Admin acting inside one, otherwise the
    // user's own company.
    const company = getActiveCompany();
    if (company) {
      config.headers["company"] = company;
    }
    const token = TokenService.getLocalAccessToken();
    if (token) {
      // config.headers["Authorization"] = 'Bearer ' + token;  // for Spring Boot back-end
      config.headers["Authorization"] = `Bearer ${token}`; // for Node.js Express back-end
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Single-flight refresh: when the access token expires, many requests 401 at
// once. If each fired its own refresh, the backend rotates the refresh token on
// the first call and rejects the rest (reuse detection), forcing a logout. So
// concurrent callers share ONE in-flight refresh and retry with its result.
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = instance
      .post("/api/auth/refresh_token", {}, { withCredentials: true })
      .then((rs) => {
        // Refresh returns accessToken as a plain string; the refresh token is
        // rotated in the httpOnly cookie.
        const accessToken = rs.data.accessToken;
        TokenService.updateLocalAccessToken(accessToken);
        instance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
        return accessToken;
      })
      .finally(() => {
        // Allow the next expiry cycle to refresh again.
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

instance.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalConfig = err.config || {};
    const url = originalConfig.url || "";
    const status = err.response?.status;
    // Never refresh-and-retry the auth endpoints themselves (avoids recursion).
    const isAuthEndpoint =
      url.includes("/api/auth/refresh_token") || url.includes("/api/auth/login");

    if (!isAuthEndpoint && status === 401 && !originalConfig._retry) {
      originalConfig._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers["Authorization"] = `Bearer ${accessToken}`;
        return instance(originalConfig);
      } catch (_error) {
        // Refresh failed unrecoverably (missing/expired/rotated refresh token).
        // A transient network failure has no response, so leave the session be.
        if (_error?.response) {
          store.dispatch(setAuthState(false));
        }
        return Promise.reject(_error);
      }
    }

    return Promise.reject(err);
  }
);

export default instance;
